import { Navigate } from "react-router";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import type { DeploymentPermissionCode } from "@/features/deployment-selection/model/permissions";
import { hasDeploymentPermission } from "@/features/deployment-selection/model/permissions";
import { getFirstAccessibleRoute } from "@/features/deployment-selection/utils/first-accessible-route";
import { Callout } from "@/shared/ui/callout";

interface PermissionGuardProps {
  /** Permission code required to access this route */
  requiredPermission: DeploymentPermissionCode;
  /** Child content to render if permission is granted */
  children: React.ReactNode;
  /** Optional: redirect to this path instead of the first accessible route */
  redirectTo?: string;
  /** Optional: show access denied message instead of redirecting */
  showAccessDenied?: boolean;
}

/**
 * Route guard that checks if the selected deployment grants a required permission.
 *
 * If the permission is not granted:
 * - Redirects to the first accessible route (default behavior)
 * - Redirects to `redirectTo` if provided
 * - Shows an access denied message if `showAccessDenied` is true
 *
 * This is a UX-only guard and does not enforce backend security.
 *
 * @example
 * ```tsx
 * <Route
 *   path="analytics"
 *   element={
 *     <PermissionGuard requiredPermission="deployment.analytics.read">
 *       <AnalyticsRoute />
 *     </PermissionGuard>
 *   }
 * />
 * ```
 */
export function PermissionGuard({
  requiredPermission,
  children,
  redirectTo,
  showAccessDenied = false,
}: PermissionGuardProps) {
  const { selectedDeployment } = useSelectedDeployment();

  // selectedDeployment should always be present because PermissionGuard
  // is used inside SelectedDeploymentGuard, but handle the edge case
  if (!selectedDeployment) {
    return <Navigate to="/deployments" replace />;
  }

  const hasPermission = hasDeploymentPermission(selectedDeployment, requiredPermission);

  if (!hasPermission) {
    // Show access denied message if explicitly requested
    if (showAccessDenied) {
      return (
        <Callout severity="warning" title="Access denied" sx={{ m: 3 }}>
          You do not have permission to access this page for deployment{" "}
          <strong>{selectedDeployment.deployment_name}</strong>.
          {selectedDeployment.access.role_keys.length > 0 && (
            <>
              <br />
              <br />
              Your current roles: {selectedDeployment.access.role_keys.join(", ")}
            </>
          )}
        </Callout>
      );
    }

    // Otherwise redirect to the specified path or the first accessible route
    const target = redirectTo ?? getFirstAccessibleRoute(selectedDeployment);
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
